import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {Card, Flex, Input, message, Popconfirm, Popover, Select, Space, Typography} from "antd";
import {useParams} from "react-router-dom";
import Title from "antd/es/typography/Title";
import useSWR from "swr";
import {api, HydraData} from "../../api/apiProvider";
import urls from "../../api/urls";
import ButtonGrey from "../ui/buttons/ButtonGrey";
import ButtonBlack from "../ui/buttons/ButtonBlack";
import {debounce} from "lodash";
import type {DefaultOptionType} from "antd/es/select";
import Roles from "../../roles/Roles";
import IconWorkgroupAction from "../ui/icons/IconWorkgroupAction";
import {BriefGroupType, BriefPosition, Channel} from "../../api/types";


interface Manager {
  email: string,
  fName: string | null,
  id: number,
  lName: string | null,
  position: string | null,
}

interface FormManager {
  brief: string,
  id: number,
  briefGroupType: string | null,
  positionInBrief: string | null,
  email: string,
  fName: string | null,
  lName: string | null,
  position: string | null,
  channels: Channel[],
}

const deleteBriefManager = async (briefManagerId: number) => {
  return await api.delete(`${urls.briefManagers}/${briefManagerId}`)
}

const addBriefManager = async (data: any) => {
  return await api.post(urls.briefManagers, {arg: data})
}

const updateBriefManager = async (briefManagerId: number, data: any) => {
  return await api.patch(`${urls.briefManagers}/${briefManagerId}`, {arg: data})
}

export default function WorkGroup() {
  const {t} = useTranslation()
  const routeParams = useParams()
  const briefId = Number(routeParams.id) || 0

  const [managersSearch, setManagersSearch] = useState<string>('')
  const [selectedEmail, setSelectedEmail] = useState<string>()
  const [selectedManagerName, setSelectedManagerName] = useState<string>()
  const [selectedManagerRole, setSelectedManagerRole] = useState<DefaultOptionType>()
  const [selectedManagerChannels, setSelectedManagerChannels] = useState<DefaultOptionType[]>()
  const [selectedBriefGroupType, setSelectedBriefGroupType] = useState<DefaultOptionType>()
  const [selectedBriefPosition, setSelectedBriefPosition] = useState<DefaultOptionType>()
  const [selectedChannels, setSelectedChannels] = useState<DefaultOptionType[]>()

  const [selectedManagerToEdit, setSelectedManagerToEdit] = useState<number>()
  const [managers, setManagers] = useState<FormManager[]>()
  const [managersOptions, setManagersOptions] = useState<DefaultOptionType[]>([])
  const [briefGroupTypeOptions, setBriefGroupTypeOptions] = useState<DefaultOptionType[]>([])
  const [channelsOptions, setChannelsOptions] = useState<DefaultOptionType[]>([])

  const getSuggestParamsString = () => {
    const params = new URLSearchParams([
      ['t[]', 'email'], ['t[]', 'manager'],
    ])

    if (managersSearch) {
      params.append('email', managersSearch)
    }

    return params.toString()
  }

  const {
    data: suggestManagers,
  } = useSWR<HydraData>(managersSearch ? `${urls.managers}?${getSuggestParamsString()}` : null, api.get, {
    revalidateOnFocus: false
  })

  const {
    data: briefManagers,
    mutate: mutateBriefManagers,
  } = useSWR<HydraData>(`${urls.briefs}/${briefId}/managers?t[]=manager&t[]=brief_manager&t[]=manager:companies&t[]=dict`, api.get, {
    revalidateOnFocus: false
  })

  const {
    data: briefGroupTypes,
  } = useSWR<HydraData>(urls.briefGroupTypes, api.get, {
    revalidateOnFocus: false
  })

  const {
    data: briefPositions,
  } = useSWR<HydraData>(`${urls.briefPositions}?t[]=dict:groupType`,
    api.get, {
      revalidateOnFocus: false
    })

  const {
    data: channels,
  } = useSWR<HydraData>(`${urls.channels}?exists[foreignId]=true`, api.get, {
    revalidateOnFocus: false
  })

  useEffect(() => {
    if (briefManagers && briefManagers.data) {
      const managerItems: FormManager[] = []

      for (let briefManager of briefManagers.data) {
        const managerItem: FormManager = {
          brief: briefManager.brief,
          id: briefManager.id,
          briefGroupType: briefManager.briefGroupType ? briefManager.briefGroupType['@id'] : null,
          positionInBrief: briefManager.positionInBrief ? briefManager.positionInBrief['@id'] : null,
          email: briefManager.manager.email,
          fName: briefManager.manager.fName,
          lName: briefManager.manager.lName,
          position: briefManager.manager.position,
          channels: briefManager.channels,
        }

        managerItems.push(managerItem)
      }

      setManagers(managerItems)
    }
  }, [briefManagers])

  useEffect(() => {
    if (suggestManagers && suggestManagers.data && suggestManagers.data.length > 0) {
      setManagersOptions(suggestManagers.data.map((suggestManager: DefaultOptionType) => {
        return {
          value: suggestManager.email,
          label: suggestManager.email,
        }
      }))
    } else {
      setSelectedEmail('');
      setManagersOptions([])
    }
  }, [suggestManagers])

  useEffect(() => {
    if (briefGroupTypes && briefGroupTypes.data && briefGroupTypes.data.length > 0) {
      setBriefGroupTypeOptions(briefGroupTypes.data.map((briefGroupType: DefaultOptionType) => {
        return {
          value: briefGroupType['@id'],
          label: briefGroupType.name,
        }
      }))
    }
  }, [briefGroupTypes])

  useEffect(() => {
    if (channels && channels.data && channels.data.length > 0) {
      setChannelsOptions(channels.data.map((channel: Channel) => {
        return {
          value: channel['@id'],
          label: channel.name,
        }
      }))
    }
  }, [channels])

  useEffect(() => {
    if (selectedEmail) {
      if (suggestManagers && suggestManagers.data) {
        const manager = suggestManagers.data.find((manager: Manager) => {
          return manager.email === selectedEmail
        })

        if (manager) {
          setSelectedManagerName(manager.fullName)
        }
      }
    }
  }, [selectedEmail])

  useEffect(() => {
    if (selectedBriefGroupType && selectedBriefGroupType.value) {
      setSelectedBriefPosition(undefined)
      setSelectedManagerToEdit(undefined)
      String(selectedBriefGroupType.value).split('/').pop()
    }
  }, [selectedBriefGroupType])

  useEffect(() => {
    if (selectedManagerToEdit && managers) {
      const manager = managers.find((manager: FormManager) => Number(manager.id) === selectedManagerToEdit)
      if (manager) {
        const position = briefPositions && briefPositions.data && briefPositions.data.find((briefPosition: BriefPosition) => briefPosition["@id"] === manager.positionInBrief)

        if (position) {
          setSelectedManagerRole({
            value: position['@id'],
            label: position.name,
          })
        }

        setSelectedManagerChannels(
          manager.channels.map((channel: Channel) => ({
            value: channel['@id'],
            label: channel.name,
          }))
        )
      }
    }
  }, [selectedManagerToEdit])

  const updateManager = () => {
    if (!Roles.hasAccess('canSetManagers') || !selectedManagerToEdit) {
      return
    }

    updateBriefManager(Number(selectedManagerToEdit), {
      positionInBrief: selectedManagerRole ? selectedManagerRole.value : null,
      channels: selectedManagerChannels ? selectedManagerChannels.map((channel: DefaultOptionType) => channel.value) : null,
    }).then(values => {
      message.success(t('common.saved'))
      mutateBriefManagers()
      setSelectedManagerToEdit(undefined)
      setSelectedChannels([])
      setSelectedManagerChannels([])
    }).catch(response => {
      message.error(response['hydra:description'])
    })
  }

  const removeManager = (managerId: number) => {
    if (managerId) {
      deleteBriefManager(managerId).then(response => {
        message.success(t('common.saved'))
        mutateBriefManagers()
      }).catch(response => {
        message.error(response['hydra:description'])
      })
    }
  }

  const addManager = () => {
    if (selectedEmail) {
      const channels = isPositionChannelsOptions(String(selectedBriefPosition?.value))
        ? selectedChannels?.map((selectedChannel) => selectedChannel.value)
        : []

      addBriefManager({
        brief: `/api${urls.briefs}/${briefId}`,
        email: selectedEmail,
        briefGroupType: selectedBriefGroupType ? selectedBriefGroupType.value : null,
        positionInBrief: selectedBriefPosition ? selectedBriefPosition.value : null,
        channels,
      }).then(response => {
        message.success(t('common.saved'))
        mutateBriefManagers()
        setSelectedEmail('')
        setSelectedBriefGroupType(undefined)
        setSelectedBriefPosition(undefined)
        setSelectedManagerChannels([])
        setSelectedChannels([])
      }).catch(response => {
        message.error(response['hydra:description'])
      })
    }
  }

  const isManagerOfType = (type: string) => {
    return managers?.some((manager: FormManager) => manager.briefGroupType === type)
  }

  const getManagerPosition = (positionIri: string) => {
    if (briefPositions && briefPositions.data && briefPositions.data.length > 0) {
      const position = briefPositions.data.find((briefPosition: BriefPosition) => {
        return briefPosition["@id"] === positionIri
      })

      if (position) {
        return {
          value: position["@id"],
          label: position.name,
        }
      }
    }

    return null
  }

  const getManagerPositionName = (positionIri: string) => {
    const position = getManagerPosition(positionIri)

    return position?.label
  }

  const getPositionOptions = (groupTypeId: string) => {
    if (briefPositions && briefPositions.data && briefPositions.data.length > 0) {
      return briefPositions.data
      .filter((briefPosition: BriefPosition) => briefPosition.briefGroupType && briefPosition.briefGroupType
        .map((groupType: BriefGroupType) => groupType["@id"]).includes(groupTypeId)
      )
      .map((briefPosition: DefaultOptionType) => {
        return {
          value: briefPosition['@id'],
          label: briefPosition.name,
        }
      })
    }

    return []
  }

  const isChannelsOptions = () => {
    if (!selectedBriefPosition) {
      return false
    }

    return isPositionChannelsOptions(String(selectedBriefPosition.value))
  }

  const isPositionChannelsOptions = (positionIri: string) => {
    if (!briefPositions || !briefPositions.data) {
      return false
    }

    const selectedPosition = briefPositions.data.find((briefPosition: BriefPosition) => briefPosition['@id'] === positionIri)

    if (selectedPosition) {

      return selectedPosition.isChannelRelation
    }

    return false
  }

  return (
    <Flex gap={40}>
      <Card style={{width: '640px'}}>
        <Space direction={'vertical'} size={'middle'} style={{width: '100%'}}>
          <Title level={5}>{t('workgroup.title')}</Title>
          <Flex vertical style={{width: '100%'}} gap={6} className={'workgroup'}>
            <Flex vertical gap={6}>
              <Typography.Text strong={true} style={{fontSize: 12}}>
                {t('workgroup.newParticipant')}:
              </Typography.Text>
              <Flex vertical gap={10}>
                <Flex gap={'small'}>
                  <Select
                    style={{width: '100%'}}
                    showSearch
                    allowClear
                    labelInValue
                    placeholder={"E-mail"}
                    //optionFilterProp="children"
                    onChange={(data: DefaultOptionType) => {
                      setSelectedEmail(data ? String(data.value) : '')
                    }}
                    onSearch={debounce((value: string) => setManagersSearch(value), 600)}
                    filterOption={false}
                    options={managersOptions}
                    value={
                      selectedEmail ?
                        {
                          value: selectedEmail,
                          label: selectedEmail,
                        } : null
                    }
                    disabled={!Roles.hasAccess('canSetManagers')}
                  />
                  {selectedManagerName &&
                    <Input
                      value={selectedManagerName}
                      disabled
                    />
                  }
                  {
                    selectedEmail && selectedBriefGroupType && selectedBriefPosition ?
                      <ButtonBlack
                        onClick={addManager}
                        disabled={!Roles.hasAccess('canSetManagers')}
                      >{t('common.add')}</ButtonBlack>
                      :
                      <ButtonGrey>{t('common.add')}</ButtonGrey>
                  }
                </Flex>
                <Flex gap={6} style={{width: '100%'}}>
                  <Flex gap={'small'} style={{width: '50%'}}>
                    <Flex vertical gap={6} style={{width: '100%'}}>
                      <Typography.Text strong={true} style={{fontSize: 12}}>
                        {t('workgroup.groupType')}:
                      </Typography.Text>
                      <Select
                        style={{width: '100%'}}
                        labelInValue
                        placeholder={t('workgroup.selectGroupType')}
                        onChange={(data: DefaultOptionType) => {
                          setSelectedBriefGroupType(data)
                        }}
                        options={briefGroupTypeOptions}
                        value={selectedBriefGroupType}
                        disabled={!Roles.hasAccess('canSetManagers') || !selectedEmail}
                      />
                    </Flex>
                  </Flex>
                  <Flex gap={'small'} style={{width: '50%'}}>
                    <Flex vertical gap={6} style={{width: '100%'}}>
                      <Typography.Text strong={true} style={{fontSize: 12}}>
                        {t('workgroup.position')}:
                      </Typography.Text>
                      <Select
                        style={{width: '100%'}}
                        labelInValue
                        placeholder={t('workgroup.selectPosition')}
                        onChange={(data: DefaultOptionType) => {
                          setSelectedBriefPosition(data)
                        }}
                        options={getPositionOptions(String(selectedBriefGroupType?.value))}
                        value={selectedBriefPosition}
                        disabled={!Roles.hasAccess('canSetManagers') || !selectedBriefGroupType}
                      />
                    </Flex>
                  </Flex>
                </Flex>
                {isChannelsOptions() &&
                  <Flex gap={6} style={{width: '100%'}}>
                    <Flex gap={'small'} style={{width: '100%'}}>
                      <Flex vertical gap={6} style={{width: '100%'}}>
                        <Typography.Text strong={true} style={{fontSize: 12}}>
                          {t('workgroup.channels')}:
                        </Typography.Text>
                        <Select
                          style={{width: '100%'}}
                          labelInValue
                          placeholder={t('workgroup.selectChannel')}
                          onChange={(data: DefaultOptionType[]) => {
                            setSelectedChannels(data)
                          }}
                          options={channelsOptions}
                          value={selectedChannels}
                          disabled={!Roles.hasAccess('canSetManagers') || !selectedEmail}
                          mode={'multiple'}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                }
              </Flex>
              <Typography.Text strong={true} style={{fontSize: 12}}>
                {t('workgroup.participants')} ({briefManagers && briefManagers.data && briefManagers.data.length || 0}):
              </Typography.Text>

              {(!briefManagers || !briefManagers.data || briefManagers.data.length === 0) &&
                <Flex className={'workgroup__empty'} justify={'center'}>
                  {t('workgroup.emptyParticipants')}
                </Flex>
              }

              <Flex vertical gap={15}>
                {briefGroupTypeOptions.map((briefGroupType: DefaultOptionType) => (
                  <Flex vertical>
                    {isManagerOfType(String(briefGroupType.value)) &&
                      <>
                        <Flex style={{paddingBottom: '5px', fontWeight: 600, fontSize: 12}}>
                          {briefGroupType.label}
                        </Flex>
                        <Flex vertical gap={5}>
                          {managers?.filter((manager: FormManager) => manager.briefGroupType === briefGroupType.value)
                          .map((manager: FormManager) => (
                            <Flex className={'workgroup__item'} vertical gap={'middle'}>
                              <Flex style={{width: '100%'}}>
                                <Flex style={{width: '100%'}} vertical>
                                  <Flex className={'workgroup__item-user'}>
                                    <Flex className={'workgroup__item-name'} align={'center'}>
                                      {manager.lName} {manager.fName}
                                    </Flex>
                                    <Flex align={'center'}>
                                      <Flex style={{width: selectedManagerToEdit === manager.id ? '80%' : ''}}>
                                        {selectedManagerToEdit !== manager.id &&
                                          getManagerPositionName(String(manager.positionInBrief))
                                        }
                                      </Flex>
                                      {selectedManagerToEdit !== manager.id &&
                                        <Flex className={'workgroup__item-email'}>
                                          [{manager.email}]
                                        </Flex>
                                      }
                                    </Flex>
                                  </Flex>
                                  {manager.channels.length > 0 && selectedManagerToEdit !== manager.id &&
                                    <Flex className={'workgroup__channels'}>
                                    <span className={'workgroup__channels-title'}>
                                      Каналы:
                                    </span>
                                      <div className={'workgroup__channels-content'}>
                                        {manager.channels.map((channel: Channel) => channel.name).join(', ')}
                                      </div>
                                    </Flex>
                                  }
                                  {selectedManagerToEdit === manager.id &&
                                    <>
                                      <Flex vertical gap={6} style={{width: '100%'}}>
                                        <Typography.Text strong={true} style={{fontSize: 12}}>
                                          {t('workgroup.position')}:
                                        </Typography.Text>
                                        <Select
                                          style={{width: '100%'}}
                                          labelInValue
                                          placeholder={t('workgroup.selectPosition')}
                                          onChange={(data: DefaultOptionType) => {
                                            setSelectedManagerRole(data)
                                          }}
                                          options={getPositionOptions(String(briefGroupType.value))}
                                          value={selectedManagerRole}
                                          disabled={!Roles.hasAccess('canSetManagers')}
                                        />
                                      </Flex>
                                      {isPositionChannelsOptions(String(selectedManagerRole?.value)) &&
                                        <Flex vertical gap={6} style={{width: '100%'}}>
                                          <Typography.Text strong={true} style={{fontSize: 12}}>
                                            {t('workgroup.channels')}:
                                          </Typography.Text>
                                          <Select
                                            style={{width: '60%'}}
                                            labelInValue
                                            placeholder={t('workgroup.selectChannel')}
                                            onChange={(data: DefaultOptionType[]) => {
                                              setSelectedManagerChannels(data)
                                            }}
                                            options={channelsOptions}
                                            value={selectedManagerChannels}
                                            disabled={!Roles.hasAccess('canSetManagers')}
                                            mode={'multiple'}
                                          />
                                        </Flex>
                                      }
                                    </>
                                  }
                                </Flex>
                                <Flex>
                                  {Roles.hasAccess('canSetManagers') &&
                                    <Popover placement="bottom" content={
                                      <>
                                        <Popconfirm
                                          placement="bottom"
                                          title={t('common.delete')}
                                          okText={t('common.yes')}
                                          cancelText={t('common.no')}
                                          onConfirm={() => removeManager(manager.id)}
                                        >
                                          <Flex className={'workgroup__item-action'}>{t('common.delete')}</Flex>
                                        </Popconfirm>
                                        <Flex className={'workgroup__item-action'}
                                              onClick={() => setSelectedManagerToEdit(manager.id)}
                                        >{t('workgroup.edit')}</Flex>
                                      </>
                                    } arrow={false}>
                                      <Flex align={'center'} gap={'small'}>
                                        <IconWorkgroupAction/>
                                      </Flex>
                                    </Popover>
                                  }
                                </Flex>
                              </Flex>
                              <Flex>
                                {selectedManagerToEdit === manager.id &&
                                  <ButtonBlack onClick={updateManager}>{t('common.save')}</ButtonBlack>
                                }
                              </Flex>
                            </Flex>
                          ))}
                        </Flex>
                      </>
                    }

                  </Flex>
                ))}
              </Flex>
            </Flex>
          </Flex>
        </Space>
      </Card>
    </Flex>
  )
}
